import { FC, memo, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import { Box, Button, Checkbox, Typography, useTheme } from '@mui/material';
import { GameStatus } from '@constants/constants';
import { FilterListItem, FilterListItemMap } from './types';
import { AddClientCodeForm, AddGameForm, SelectableList } from '../Sidepanel';

interface FilterGroupsListProps {
  filterMap: FilterListItemMap;
  selectedIds: string[];
  onSelectionChange: (ids: string[]) => void;
  addGame?: (gameId: string) => Promise<boolean>;
  addClientCode?: (clientCode: string) => Promise<boolean>;
  isAddingGame?: boolean;
  existingGameIds: string[];
  existingClientCodes?: string[];
  /** When true, the entire list becomes non-interactive (Full Access active or view-only). */
  isDisabled?: boolean;
  /** When true, checkboxes and the Add Game button are disabled but the list remains visible. */
  isViewOnly?: boolean;
}

export interface FilterGroupSelectionItem {
  type: string;
  options: { id: string; name: string; submoduleFilterId: string; filterValue: string }[];
}

/**
 * Derives the grouped save payload from the flat selectedIds.
 * Shape matches FilterSection / ModulesSubModules handleFilterSave.
 */
export const buildFilterGroupSelection = (
  filterMap: FilterListItemMap,
  selectedIds: string[],
): FilterGroupSelectionItem[] => {
  const selectedSet = new Set(selectedIds);
  return Object.entries(filterMap).map(([key, items]) => ({
    type: key,
    options: items
      .filter((item) => selectedSet.has(item.id))
      .map((item) => ({
        id: item.id,
        name: item.label ? `${item.label} - ${item.value}` : item.value,
        submoduleFilterId: item.meta?.submoduleFilterId ?? '',
        filterValue: item.meta?.filterValue ?? item.value,
      })),
  }));
};

const filterListItem = (item: FilterListItem, search: string): boolean => {
  const query = search.toLowerCase();
  return (
    item.value.toLowerCase().includes(query) || (item.label?.toLowerCase().includes(query) ?? false)
  );
};

const FILTER_LABEL_MAP: Record<string, string> = {
  game_id: 'Game ID',
  client_code: 'Client Code',
  category_genre: 'Category/Genre',
};

const getFilterName = (key: string): string => FILTER_LABEL_MAP[key] ?? key;

const getFundedStatusLabel = (status?: string): string => {
  if (!status) return '-';
  switch (status) {
    case GameStatus.FUNDED:
      return 'Funded';
    case GameStatus.NOT_FUNDED:
      return 'Not Funded';
    case GameStatus.PAUSED_FUNDING:
      return 'Paused Funding';
    default:
      return status;
  }
};

const FilterItemRow: FC<{
  item: FilterListItem;
  isSelected: boolean;
  onToggle: () => void;
  disabled?: boolean;
}> = memo(({ item, isSelected, onToggle, disabled }) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '36px 80px 1fr auto',
        alignItems: 'center',
        py: '6px',
        borderBottom: '1px solid #6C6C6C',
        '&:last-of-type': { borderBottom: 'none' },
      }}
    >
      <Checkbox
        checked={isSelected}
        onChange={onToggle}
        disabled={disabled}
        size="small"
        sx={{
          p: 0,
          color: theme.palette.primary.main,
          '&.Mui-checked': { color: theme.palette.primary.main },
        }}
      />
      <Typography fontSize={15} fontWeight={500} sx={{ color: '#162936', whiteSpace: 'nowrap' }}>
        {item.value}
      </Typography>
      <Typography
        fontSize={16}
        fontWeight={500}
        sx={{
          color: '#162936',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          pr: 1,
        }}
      >
        {item.label ?? ''}
      </Typography>
      <Typography fontSize={15} fontWeight={500} sx={{ color: '#162936', whiteSpace: 'nowrap' }}>
        {getFundedStatusLabel(item.status)}
      </Typography>
    </Box>
  );
});

FilterItemRow.displayName = 'FilterItemRow';

const FilterGroupsList: FC<FilterGroupsListProps> = ({
  filterMap,
  selectedIds,
  onSelectionChange,
  addGame,
  addClientCode,
  isAddingGame,
  existingGameIds,
  existingClientCodes = [],
  isDisabled = false,
  isViewOnly = false,
}) => {
  const [showAddGameForm, setShowAddGameForm] = useState(false);
  const [newGameId, setNewGameId] = useState('');
  const [showAddClientCodeForm, setShowAddClientCodeForm] = useState(false);
  const [newClientCode, setNewClientCode] = useState('');

  const isDuplicateGameId = Boolean(newGameId) && existingGameIds.includes(newGameId);
  const isDuplicateClientCode =
    Boolean(newClientCode) && existingClientCodes.includes(newClientCode);

  const handleAddGame = async () => {
    if (!newGameId || isDuplicateGameId) return;
    const success = await addGame?.(newGameId);
    if (success) {
      setNewGameId('');
      setShowAddGameForm(false);
    }
  };

  const handleAddClientCode = async () => {
    if (!newClientCode || isDuplicateClientCode) return;
    const success = await addClientCode?.(newClientCode);
    if (success) {
      setNewClientCode('');
      setShowAddClientCodeForm(false);
    }
  };

  const handleGroupSelectionChange = (groupItems: FilterListItem[], newGroupIds: string[]) => {
    const groupIdSet = new Set(groupItems.map((item) => item.id));
    const otherIds = selectedIds.filter((id) => !groupIdSet.has(id));
    onSelectionChange([...otherIds, ...newGroupIds]);
  };

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        overflow: 'hidden',
        opacity: isDisabled ? 0.5 : 1,
        pointerEvents: isDisabled ? 'none' : 'auto',
      }}
    >
      {Object.entries(filterMap).map(([key, items]) => {
        const isGameIdGroup = key === 'game_id';
        const isClientCodeGroup = key === 'client_code';
        const groupIdSet = new Set(items.map((item) => item.id));
        const groupSelectedIds = selectedIds.filter((id) => groupIdSet.has(id));

        return (
          <Box key={key}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 0.5,
              }}
            >
              <Typography fontSize={14} fontWeight={700} sx={{ color: '#162936' }}>
                {getFilterName(key)}
              </Typography>

              {isGameIdGroup && !showAddGameForm && addGame && (
                <Button
                  onClick={() => setShowAddGameForm(true)}
                  disabled={isViewOnly}
                  disableRipple
                  sx={{
                    color: '#51B8B0',
                    textTransform: 'none',
                    p: 0,
                    minWidth: 'unset',
                    gap: '4px',
                    '&:hover': { background: 'transparent' },
                  }}
                >
                  <AddIcon sx={{ height: '1rem', width: '1rem' }} />
                  <Typography fontSize={14} fontWeight={700} sx={{ color: '#51B8B0' }}>
                    Add Game
                  </Typography>
                </Button>
              )}

              {isClientCodeGroup && !showAddClientCodeForm && addClientCode && (
                <Button
                  onClick={() => setShowAddClientCodeForm(true)}
                  disabled={isViewOnly}
                  disableRipple
                  sx={{
                    color: '#51B8B0',
                    textTransform: 'none',
                    p: 0,
                    minWidth: 'unset',
                    gap: '4px',
                    '&:hover': { background: 'transparent' },
                  }}
                >
                  <AddIcon sx={{ height: '1rem', width: '1rem' }} />
                  <Typography fontSize={14} fontWeight={700} sx={{ color: '#51B8B0' }}>
                    Add Client Code
                  </Typography>
                </Button>
              )}
            </Box>

            {isGameIdGroup && showAddGameForm && (
              <AddGameForm
                value={newGameId}
                onChange={setNewGameId}
                onSave={handleAddGame}
                onCancel={() => {
                  setShowAddGameForm(false);
                  setNewGameId('');
                }}
                isDuplicate={isDuplicateGameId}
                isLoading={isAddingGame || false}
              />
            )}

            {isClientCodeGroup && showAddClientCodeForm && (
              <AddClientCodeForm
                value={newClientCode}
                onChange={setNewClientCode}
                onSave={handleAddClientCode}
                onCancel={() => {
                  setShowAddClientCodeForm(false);
                  setNewClientCode('');
                }}
                isDuplicate={isDuplicateClientCode}
                isLoading={isAddingGame || false}
              />
            )}

            <SelectableList
              items={items}
              selectedIds={groupSelectedIds}
              onSelectionChange={(newGroupIds) => handleGroupSelectionChange(items, newGroupIds)}
              getItemId={(item) => item.id}
              filterItem={filterListItem}
              renderRow={(item, isSelected, onToggle) => (
                <FilterItemRow
                  key={item.id}
                  item={item}
                  isSelected={isSelected}
                  onToggle={onToggle}
                  disabled={isViewOnly}
                />
              )}
              searchPlaceholder={`Search ${getFilterName(key)}`}
            />
          </Box>
        );
      })}
    </Box>
  );
};

export default FilterGroupsList;
